import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Users, Bell, Bot, AlertTriangle } from 'lucide-react';

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [agents, setAgents] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [voices, setVoices] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState('Dashboard');
  const [search, setSearch] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [loading, setLoading] = useState(true);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [orphanAssign, setOrphanAssign] = useState({});
  const [createUser, setCreateUser] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [dash, u, ag, audr, p] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/users'),
        api.get('/admin/agents'),
        api.get('/admin/audit?limit=50'),
        api.get('/admin/users?role=partner'),
      ]);
      setStats(dash.data.stats);
      setUsers(u.data.users);
      setAgents(ag.data.agents);
      setAudit(audr.data.logs);
      setPartners(p.data.users);
    } catch {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadOrphans = async () => {
    try {
      const r = await api.get('/admin/agents/orphans');
      setOrphans(r.data.orphans);
    } catch {
      toast.error('Failed to load orphans');
    }
  };

  useEffect(() => {
    if (tab === 'Unassigned') loadOrphans();
  }, [tab]);

  // Voice names for the Unassigned table — loaded once, on first visit to that tab.
  useEffect(() => {
    if (tab !== 'Unassigned' || voices.length) return;
    api.get('/agents/meta/voices')
      .then(res => setVoices(res.data.voices || []))
      .catch(() => {});
  }, [tab, voices.length]);

  const searchUsers = async () => {
    try {
      const res = await api.get(`/admin/users?search=${search}`);
      setUsers(res.data.users);
    } catch {}
  };

  const userAction = async (userId, action) => {
    const reasons = { ban: prompt('Reason for ban:'), deactivate: undefined, delete: undefined };
    try {
      await api.post(`/admin/users/${userId}/${action}`, { reason: reasons[action] });
      toast.success(`User ${action}d`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    }
  };

  const changeParent = async (userId, parent_user_id) => {
    try {
      await api.patch(`/admin/users/${userId}/parent`, { parent_user_id });
      toast.success('Parent updated');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role });
      toast.success(`Role → ${role}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const reassignAgent = async (agentId, user_id) => {
    try {
      await api.patch(`/admin/agents/${agentId}/owner`, { user_id });
      toast.success('Owner reassigned');
      setReassignTarget(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const assignOrphan = async (telnyxId) => {
    const user_id = orphanAssign[telnyxId];
    if (!user_id) return toast.error('Pick a user');
    try {
      await api.post(`/admin/agents/orphans/${telnyxId}/assign`, { user_id });
      toast.success('Owner assigned');
      setOrphanAssign(prev => ({ ...prev, [telnyxId]: '' }));
      loadOrphans();
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const submitCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/users', createUser);
      toast.success('User created');
      setCreateUser(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const postAnnouncement = async () => {
    if (!announcement.trim()) return;
    try {
      await api.post('/admin/announcements', { message: announcement, expires_days: 7 });
      toast.success('Announcement posted');
      setAnnouncement('');
    } catch {
      toast.error('Failed to post announcement');
    }
  };

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  return (
    <div className="page">
      <h1 className="page-title">Admin Panel</h1>

      <div className="tabs">
        {['Dashboard', 'Users', 'Agents', 'Unassigned', 'Audit Log', 'Announcements'].map(t => (
          <button key={t} className={`tab ${tab === t ? 'tab--active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'Dashboard' && stats && (
        <div className="tab-content">
          <div className="stats-grid">
            <div className="stat-card"><span className="stat-value">{stats.total_users}</span><span className="stat-label">Total Users</span></div>
            <div className="stat-card"><span className="stat-value">{stats.demo_users}</span><span className="stat-label">Demo Users</span></div>
            <div className="stat-card"><span className="stat-value">{stats.active_users}</span><span className="stat-label">Paid Users</span></div>
            <div className="stat-card"><span className="stat-value">{stats.total_partners}</span><span className="stat-label">Partners</span></div>
            <div className="stat-card"><span className="stat-value">{stats.total_agents}</span><span className="stat-label">Active Agents</span></div>
            <div className="stat-card"><span className="stat-value">{stats.total_calls}</span><span className="stat-label">Total Calls</span></div>
            <div className="stat-card"><span className="stat-value">{stats.calls_today}</span><span className="stat-label">Calls Today</span></div>
            <div className="stat-card"><span className="stat-value">${(stats.revenue_this_month || 0).toFixed(2)}</span><span className="stat-label">Revenue (month)</span></div>
          </div>
        </div>
      )}

      {tab === 'Users' && (
        <div className="tab-content">
          <div className="search-bar">
            <input className="input" placeholder="Search by email or name..."
              value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchUsers()} />
            <button className="btn btn-primary" onClick={searchUsers}>Search</button>
            <button className="btn" onClick={() => setCreateUser({ email: '', password: '', full_name: '', role: 'owner', plan: 'demo' })}>
              <Users size={14} /> Add User
            </button>
          </div>

          {createUser && (
            <form className="form-section" onSubmit={submitCreateUser}>
              <h3>Create User</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input className="input" type="email" required value={createUser.email}
                    onChange={e => setCreateUser({ ...createUser, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input className="input" required value={createUser.full_name}
                    onChange={e => setCreateUser({ ...createUser, full_name: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password *</label>
                  <input className="input" type="password" minLength={8} required value={createUser.password}
                    onChange={e => setCreateUser({ ...createUser, password: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select className="input" value={createUser.role}
                    onChange={e => setCreateUser({ ...createUser, role: e.target.value })}>
                    <option value="owner">Owner</option>
                    <option value="partner">Partner</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              {createUser.role === 'owner' && (
                <div className="form-group">
                  <label>Parent partner (optional)</label>
                  <select className="input" value={createUser.parent_user_id || ''}
                    onChange={e => setCreateUser({ ...createUser, parent_user_id: e.target.value || undefined })}>
                    <option value="">— none (standalone) —</option>
                    {partners.map(p => <option key={p.id} value={p.id}>{p.email}</option>)}
                  </select>
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setCreateUser(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          )}

          <table className="data-table">
            <thead>
              <tr><th>Email</th><th>Name</th><th>Role</th><th>Partner</th><th>Plan</th><th>Balance</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.full_name}</td>
                  <td>
                    <select className="input input-sm" value={u.role || 'owner'}
                      onChange={e => changeRole(u.id, e.target.value)}>
                      <option value="owner">owner</option>
                      <option value="partner">partner</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    {u.role === 'owner' ? (
                      <select className="input input-sm" value={u.parent_user_id || ''}
                        onChange={e => changeParent(u.id, e.target.value || null)}>
                        <option value="">— none —</option>
                        {partners.filter(p => p.id !== u.id).map(p => (
                          <option key={p.id} value={p.id}>{p.email}</option>
                        ))}
                      </select>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td><span className="badge">{u.plan}</span></td>
                  <td>${(u.balance || 0).toFixed(2)}</td>
                  <td><span className={`status-badge status-badge--${u.status}`}>{u.status}</span></td>
                  <td>
                    <div className="action-btns">
                      <button className="btn btn-sm btn-warning" onClick={() => userAction(u.id, 'deactivate')}>Suspend</button>
                      <button className="btn btn-sm btn-danger-ghost" onClick={() => userAction(u.id, 'ban')}>Ban</button>
                      <button className="btn btn-sm btn-danger" onClick={() => userAction(u.id, 'delete')}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Agents' && (
        <div className="tab-content">
          <h3>All Agents ({agents.length})</h3>
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Owner</th><th>Partner</th><th>Type</th><th>Calls</th><th>Status</th><th>Telnyx</th><th>Reassign</th></tr>
            </thead>
            <tbody>
              {agents.map(a => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td>{a.owner_email} <small style={{ color: '#888' }}>({a.owner_role})</small></td>
                  <td>{a.partner_email || '—'}</td>
                  <td><span className={`badge badge--${a.type}`}>{a.type}</span></td>
                  <td>{a.call_count}</td>
                  <td><span className={`status-badge status-badge--${a.status}`}>{a.status}</span></td>
                  <td>{a.telnyx_synced ? '✓' : <span className="badge badge--warning">offline</span>}</td>
                  <td>
                    {reassignTarget === a.id ? (
                      <select className="input input-sm" autoFocus
                        onBlur={() => setReassignTarget(null)}
                        onChange={e => reassignAgent(a.id, e.target.value)}>
                        <option value="">— pick user —</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.email}</option>)}
                      </select>
                    ) : (
                      <button className="btn btn-sm" onClick={() => setReassignTarget(a.id)}>Reassign</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Unassigned' && (
        <div className="tab-content">
          <div className="banner banner--info">
            <AlertTriangle size={16} />
            <span>Telnyx assistants without a recognized owner. Assign each to a user — owner will be persisted to Telnyx description.</span>
          </div>
          <button className="btn" onClick={loadOrphans}>Refresh</button>

          {orphans.length === 0 ? (
            <div className="empty-state">
              <Bot size={48} className="empty-icon" />
              <h3>No unassigned agents</h3>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Telnyx ID</th><th>Name</th><th>Voice</th><th>Has marker</th><th>Assign to</th></tr>
              </thead>
              <tbody>
                {orphans.map(o => (
                  <tr key={o.telnyx_agent_id}>
                    <td><code style={{ fontSize: 11 }}>{o.telnyx_agent_id}</code></td>
                    <td>{o.name}</td>
                    <td><small>{voices.find(v => v.id === o.voice)?.name || o.voice || '—'}</small></td>
                    <td>{o.has_meta_marker ? <span className="badge badge--warning">stale</span> : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select className="input input-sm" value={orphanAssign[o.telnyx_agent_id] || ''}
                          onChange={e => setOrphanAssign(prev => ({ ...prev, [o.telnyx_agent_id]: e.target.value }))}>
                          <option value="">— pick user —</option>
                          {users.map(u => <option key={u.id} value={u.id}>{u.email}</option>)}
                        </select>
                        <button className="btn btn-sm btn-primary" onClick={() => assignOrphan(o.telnyx_agent_id)}>Assign</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'Audit Log' && (
        <div className="tab-content">
          <table className="data-table">
            <thead><tr><th>Time</th><th>User</th><th>Action</th><th>IP</th></tr></thead>
            <tbody>
              {audit.map(log => (
                <tr key={log.id}>
                  <td>{new Date(log.created_at * 1000).toLocaleString()}</td>
                  <td>{log.user_id?.slice(0, 8)}...</td>
                  <td><code>{log.action}</code></td>
                  <td>{log.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Announcements' && (
        <div className="tab-content">
          <div className="form-section">
            <h3>Post System Announcement</h3>
            <div className="form-group">
              <label>Message (shown to all users for 7 days)</label>
              <textarea className="input textarea" rows={3} value={announcement}
                onChange={e => setAnnouncement(e.target.value)}
                placeholder="e.g. Scheduled maintenance on Nov 5 from 3:00-4:00 AM AEST" />
            </div>
            <button className="btn btn-primary" onClick={postAnnouncement}>
              <Bell size={16} /> Post Announcement
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
